import { Alert, Box, Button, PasswordInput, Stack, Text, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDocumentTitle } from '@mantine/hooks';
import { IconAlertCircle, IconArrowRight } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { businessToday } from '@mexp/shared';
import { errorMessage } from '@/api/client';
import { useAuth } from '@/auth/AuthProvider';
import { FullPageLoader } from '@/auth/guards';
import { homeFor, INTERNAL, safeNext } from '@/lib/paths';
import { NeededByTimeline, type TimelineItem } from './NeededByTimeline';
import { PortalMark } from './PortalMark';
import { addDays } from './stage';
import './portal.css';

/** Illustrative open requests for the sign-in field, dated around today and labelled as an example. */
function exampleItems(today: string): TimelineItem[] {
  return [
    { id: 'e1', title: 'Zinc syrup', place: 'Kenya', detail: 'Example', requiredBy: addDays(today, -3), stage: 'completed' },
    { id: 'e2', title: 'Iron softgels', place: 'Ghana', detail: 'Example', requiredBy: addDays(today, 14), stage: 'in_preparation' },
    { id: 'e3', title: 'Omega-3', place: 'Vietnam', detail: 'Example', requiredBy: addDays(today, 30), stage: 'final_review' },
    { id: 'e4', title: 'Cough syrup', place: 'Sri Lanka', detail: 'Example', requiredBy: addDays(today, 46), stage: 'received' },
  ];
}

/** Client sign-in (/login). Logins are issued by Makin; there is no sign-up or self-service reset. */
export function ClientLoginPage() {
  useDocumentTitle('Sign in · Makin RA Portal');
  const { status, user, login } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const today = businessToday();
  const examples = useMemo(() => exampleItems(today), [today]);
  const form = useForm({
    initialValues: { email: '', password: '' },
    validate: {
      email: (v) => (/^\S+@\S+\.\S+$/.test(v.trim()) ? null : 'Enter the email address Makin registered for you'),
      password: (v) => (v ? null : 'Enter your password'),
    },
  });
  const next = safeNext(params.get('next'), '/');

  if (status === 'loading') return <FullPageLoader />;
  if (status === 'authenticated' && user) {
    return <Navigate to={user.userType === 'exporter' ? next : `${INTERNAL}${next === '/' ? '' : next}`} replace />;
  }

  const submit = form.onSubmit(async (values) => {
    setSubmitting(true);
    setError(null);
    try {
      const signedIn = await login(values.email.trim(), values.password);
      // Makin staff who land here go to their own workspace.
      navigate(signedIn.userType === 'exporter' ? next : homeFor(signedIn), {
        replace: true,
      });
    } catch (e) {
      setError(errorMessage(e));
      form.setFieldValue('password', '');
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <Box className="mexp-portal">
      <Box className="portal-login">
        <Box component="section" className="portal-field portal-login-field" p={{ base: 'lg', md: 48 }}>
          <Stack justify="space-between" h="100%" gap="xl">
            <PortalMark inverse />
            <Stack gap="lg" maw={560}>
              <Title order={1} c="white" fz={{ base: 28, md: 42 }} lh={1.12} fw={680} style={{ letterSpacing: '-0.02em', textWrap: 'balance' }}>
                Registration documents, tracked from requisition to download.
              </Title>
              <Text fz={{ base: 'md', md: 18 }} c="brand.1" lh={1.55} maw={500} style={{ textWrap: 'pretty' }}>
                Send Makin a dossier requisition, follow each dossier to the date Makin gives you, and download the documents once Makin has approved them.
              </Text>
            </Stack>
            <Box visibleFrom="md">
              <Box
                p="lg"
                style={{
                  borderRadius: 16,
                  background: 'rgba(0, 45, 64, 0.28)',
                  boxShadow: 'inset 0 0 0 1px rgba(166, 207, 223, 0.18)',
                }}
              >
                <NeededByTimeline items={examples} today={today} inverse weeksAhead={7} />
                <Text fz={12} c="brand.2" mt={4}>
                  Example of how your dossiers line up by their expected dates.
                </Text>
              </Box>
            </Box>
            <Text fz={12} c="brand.1" visibleFrom="md">
              Makin Laboratories Pvt. Ltd. · Pithampur, Indore
            </Text>
          </Stack>
        </Box>

        <Box component="main" className="portal-login-form" p={{ base: 'lg', md: 48 }}>
          <Stack w="100%" maw={380} gap="lg">
            <Stack gap={6}>
              <Title order={2} fz={28} style={{ letterSpacing: '-0.01em' }}>
                Sign in
              </Title>
              <Text c="dimmed" fz="sm">
                Use the email and password Makin set up for you.
              </Text>
            </Stack>

            <form onSubmit={submit} noValidate>
              <Stack gap="md">
                {error && (
                  <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} title="Could not sign you in">
                    {error}
                  </Alert>
                )}
                <TextInput
                  label="Email"
                  placeholder="you@yourcompany.com"
                  autoComplete="username"
                  autoFocus
                  type="email"
                  size="md"
                  {...form.getInputProps('email')}
                />
                <PasswordInput label="Password" autoComplete="current-password" size="md" {...form.getInputProps('password')} />
                <Button type="submit" loading={submitting} fullWidth size="md" rightSection={<IconArrowRight size={18} />}>
                  Sign in
                </Button>
              </Stack>
            </form>

            <Text c="dimmed" fz="sm" lh={1.55}>
              No login yet, or forgot your password? Your Makin account manager can set one up or reset it for you.
            </Text>
            <Text c="dimmed" fz={11} hiddenFrom="md">
              Makin Laboratories Pvt. Ltd. · Pithampur, Indore
            </Text>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}
